/**
 * Выезжающая справа панель: затемнение слева, `aside` на всю высоту, шапка с
 * подписью и крестиком, Esc — стопкой слоёв.
 *
 * До 14.10.26 эта обвязка стояла копией в семи панелях (`InfoPanel`,
 * `OrdersEtaPanel`, `PlansPanel`, `OrderPanel`, `CargoPanel`, `CargoChatPanel`,
 * `PositionReceivedPanel`; `PlansPanel` так и писал: «копия `OrdersEtaPanel`»),
 * и правило «Esc закрывает верхнюю панель, затемнение — кликом» жило в семи
 * местах (аудит фронта 12.10.26, §5.2).
 *
 * ESC — ЧЕРЕЗ `useEscapeLayer`, а не своим слушателем: панели ложатся стопкой
 * («инфо» товара → «в заказах» → карточка заказа → накладная), и свой
 * слушатель окна проигрывал бы слушателю стопки на захвате — Esc закрыл бы
 * панель ПОД нами. `onEscape` — когда Esc сначала закрывает внутреннюю
 * ступень (форму статуса) и панель остаётся: вернуть `false`.
 *
 * СЛОЙ И ПОРТАЛ — панели поверх панели: «в заказах» (`z-50`) открывается из
 * карточки, которая сама бывает в панели «инфо» (`z-40`), карточка заказа —
 * ещё выше (`z-[60]`). Порталом в `body`, чтобы не зависеть от `overflow` и
 * контекста наложения хозяина.
 *
 * ТЕЛО — на вызывающем: карточка товара и заказа держат свою прокрутку внутри,
 * прочие панели прокручивают тело целиком (`overflow-y-auto p-4`).
 */

import { X } from "lucide-react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";

import { useEscapeLayer } from "@/lib/escapeStack";

/** Классы — буквально: Tailwind собирает только то, что видит в исходнике. */
const СЛОЙ = { 40: "z-40", 50: "z-50", 60: "z-[60]" } as const;

export function SidePanel({
  title,
  actions,
  width,
  z = 40,
  portal = false,
  annot,
  onClose,
  onEscape,
  children,
}: {
  /** Подпись шапки — обрезается с многоточием. */
  title: ReactNode;
  /** Ссылки шапки — справа, перед крестиком. */
  actions?: ReactNode;
  /** Класс ширины `aside`: `w-[75vw]`, `w-[min(820px,94vw)]`. */
  width: string;
  /** Слой: 40 — панель раздела, 50 — поверх неё, 60 — поверх той. */
  z?: keyof typeof СЛОЙ;
  /** Порталом в `body` — панель, открываемая изнутри другой панели. */
  portal?: boolean;
  /** `data-annot` корня — адрес для разметки владельца (`Annotator`). */
  annot: string;
  onClose: () => void;
  /** Esc, если он не просто закрывает: `false` — панель остаётся верхней. */
  onEscape?: () => void | boolean;
  children: ReactNode;
}) {
  useEscapeLayer(onEscape ?? onClose);
  const close = (
    <button
      type="button"
      onClick={onClose}
      title="закрыть (Esc)"
      className={`${actions ? "" : "ml-auto "}flex-none rounded border border-border px-1.5 py-1 text-fg-muted hover:text-fg`}
    >
      <X size={14} />
    </button>
  );
  const panel = (
    <div className={`fixed inset-0 ${СЛОЙ[z]} flex`} data-annot={annot}>
      <div className="flex-1 bg-fg/20" onClick={onClose} />
      <aside className={`flex h-full ${width} flex-col overflow-hidden border-l border-border bg-bg shadow-2xl`}>
        <div className="flex flex-none items-center gap-3 border-b border-border px-4 py-2" data-annot="шапка панели">
          <span className="min-w-0 truncate text-[14px] font-medium">{title}</span>
          {actions ? (
            <div className="ml-auto flex flex-none items-center gap-3">
              {actions}
              {close}
            </div>
          ) : (
            close
          )}
        </div>
        {children}
      </aside>
    </div>
  );
  return portal ? createPortal(panel, document.body) : panel;
}
